import React, { useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { Formik } from 'formik'
import axios from 'axios'
import classNames from 'classnames'
import Imgix from 'react-imgix'
import { Form as RBForm } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useNotificationService } from '@abroad/components'
import { ExperienceInputField } from '../form'
import API from '../../utils/API'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'
import ExperienceButton from './ExperienceButton'
import ExperienceConfirmModal from './ExperienceConfirmModal'

export const PARTNER_ROW_DND = 'PARTNER_ROW'

export const PartnerCard = ({
  partner,
  index,
  isEditing,
  onEdit,
  onDelete,
  moveItem,
  onDragEnd,
  isUploading,
}) => {
  const ref = useRef(null)

  const [{ handlerId }, drop] = useDrop({
    accept: PARTNER_ROW_DND,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(item, monitor) {
      if (!ref.current || isUploading) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: PARTNER_ROW_DND,
    item: () => ({ index }),
    canDrag: !isUploading,
    end() {
      if (!isUploading && typeof onDragEnd === 'function') {
        onDragEnd()
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const handleCardClick = () => {
    onEdit(partner, index)
  }

  const handleActionClick = (e, action) => {
    e.stopPropagation()
    action()
  }

  const logoFileName = partner.logoFileName

  return (
    <div
      ref={ref}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={classNames('experience-fac', {
        'is-editing': isEditing,
        'is-dragging': isDragging,
      })}
      data-handler-id={handlerId}
      onClick={handleCardClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleCardClick()
        }
      }}>
      <span
        className='experience-grab'
        title='Drag to reorder'
        onClick={(e) => e.stopPropagation()}>
        ⋮⋮
      </span>
      {logoFileName ? (
        logoFileName.startsWith('data:') ? (
          <img
            src={logoFileName}
            alt=''
            className='experience-fac-av'
            style={{ objectFit: 'contain' }}
          />
        ) : (
          <Imgix
            imgixParams={{
              fit: 'contain',
              auto: 'format,compress',
              q: 90,
            }}
            src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${logoFileName}`}
            width={42}
            height={42}
            alt=''
            className='experience-fac-av lazyload'
            htmlAttributes={{
              style: { objectFit: 'contain' },
            }}
          />
        )
      ) : (
        <div className='experience-fac-av is-empty' aria-hidden='true' />
      )}
      <div className='experience-fac-copy'>
        <b>{partner.title || 'Unnamed partner'}</b>
        <span>{partner.description || 'No description yet'}</span>
      </div>
      <button
        type='button'
        className='experience-x'
        title='Delete'
        aria-label='Delete'
        disabled={isUploading}
        onClick={(e) => handleActionClick(e, () => onDelete(index))}>
        <ExperienceDeleteIcon />
      </button>
    </div>
  )
}

export const PartnerEditor = ({
  partner,
  partnerIndex,
  onSave,
  onCancel,
  isSaving = false,
  onUploadingChange,
}) => {
  const imageRef = useRef()
  const { questId } = useParams()
  const Notification = useNotificationService()
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [showRemoveImageConfirm, setShowRemoveImageConfirm] = useState(false)
  const pendingRemoveRef = useRef(null)

  const setUploading = (value) => {
    setIsUploading(value)
    if (onUploadingChange) onUploadingChange(value)
  }

  const handlePosterClick = () => {
    if (imageRef.current) {
      imageRef.current.value = ''
      imageRef.current.click()
    }
  }

  const validateImageFile = (imageFile) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()
    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }

    if (
      imageFile.type === 'image/webp' ||
      imageFile.name.toLowerCase().endsWith('.webp')
    ) {
      Notification.showNotification(
        'WebP format is not supported. Please use JPG, JPEG, or PNG format.',
        'danger',
      )
      return false
    }
    return true
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDraggingImage) setIsDraggingImage(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingImage(false)
    }
  }

  const uploadPartnerLogo = async (imageFile) => {
    const contentType = imageFile.name
      .substr(imageFile.name.lastIndexOf('.') + 1)
      .toUpperCase()

    const { data } = await API.AdminAPI.quests.getPartnersSignedURL(
      questId,
      contentType,
    )
    if (!data) {
      throw new Error('Image upload failed')
    }

    await axios.put(data.url.url, imageFile, {
      headers: {
        'Content-Type': data.url.contentType,
      },
    })

    return data.fileName
  }

  const uploadSelectedLogo = async (imageFile, setFieldValue, resetInput) => {
    if (!imageFile || !validateImageFile(imageFile)) {
      if (resetInput) resetInput()
      return
    }

    setFieldValue('isUploading', true)
    setUploading(true)
    const reader = new FileReader()
    reader.onerror = () => {
      setFieldValue('isUploading', false)
      setUploading(false)
      if (resetInput) resetInput()
    }
    reader.onloadend = async () => {
      setFieldValue('logoFileName', reader.result)
      try {
        const fileName = await uploadPartnerLogo(imageFile)
        setFieldValue('logoFileName', fileName)
        setFieldValue('isUploading', false)
        setUploading(false)
      } catch (e) {
        setFieldValue('isUploading', false)
        setUploading(false)
        Notification.showNotification('Image upload failed', 'danger')
      } finally {
        setIsDraggingImage(false)
        if (resetInput) resetInput()
      }
    }
    reader.readAsDataURL(imageFile)
  }

  const handleDrop = (e, setFieldValue) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      uploadSelectedLogo(files[0], setFieldValue)
      e.dataTransfer.clearData()
    }
  }

  const initialValues = {
    title: partner?.title || '',
    description: partner?.description || '',
    logoFileName: partner?.logoFileName || '',
    name: partner?.name || partner?.title || '',
    sequenceNumber:
      typeof partner?.sequenceNumber === 'number'
        ? partner.sequenceNumber
        : partnerIndex,
    isUploading: false,
  }

  const handleFormSubmit = (values) => {
    if (onSave) {
      onSave({
        ...values,
        name: values.title || values.name || '',
        isUploading: undefined,
      })
    }
  }

  const handleRemoveImage = (setFieldValue) => {
    setFieldValue('logoFileName', '')
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleFormSubmit}>
      {(formik) => {
        const { handleSubmit, setFieldValue, values } = formik
        const hasImage = !!values.logoFileName
        const busy = values?.isUploading || isUploading

        return (
          <form onSubmit={handleSubmit} aria-busy={busy || undefined}>
            <div className='experience-fac-editor'>
              <div>
                <ExperienceInputField
                  name='title'
                  label='Title'
                  placeholder='Add partner name'
                  capitalizeFirstLetter
                  showErrorMessage={false}
                  displayFlex={false}
                  disabled={busy}
                />
                <div className='mt-12px'>
                  <ExperienceInputField
                    name='description'
                    label='Description'
                    placeholder='Add a short description'
                    as='textarea'
                    rows={3}
                    capitalizeFirstLetter
                    showErrorMessage={false}
                    displayFlex={false}
                    disabled={busy}
                  />
                </div>
              </div>

              <div className='experience-f'>
                <label className='experience-f-label'>Logo</label>
                <div className='experience-img-slot' data-ratio='1:1'>
                  <div
                    className={classNames('experience-img-frame', {
                      'is-empty': !hasImage && !busy,
                      'is-dragging': isDraggingImage,
                    })}
                    onClick={() => {
                      if (!busy) handlePosterClick()
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, setFieldValue)}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (!busy) handlePosterClick()
                      }
                    }}>
                    {busy ? (
                      <span
                        className='experience-img-hover'
                        style={{ opacity: 1 }}>
                        Uploading…
                      </span>
                    ) : (
                      <span className='experience-img-hover'>
                        {isDraggingImage
                          ? 'Drop image to upload'
                          : hasImage
                          ? 'Click to replace'
                          : 'Upload image'}
                      </span>
                    )}

                    {hasImage ? (
                      values.logoFileName.startsWith('data:') ? (
                        <img src={values.logoFileName} alt='partner logo' />
                      ) : (
                        <Imgix
                          src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${values.logoFileName}`}
                          imgixParams={{
                            fit: 'contain',
                            auto: 'format,compress',
                          }}
                          width={'100%'}
                          alt='partner logo'
                          className={classNames('lazyload', {
                            itenaryImage: busy,
                          })}
                          htmlAttributes={{
                            style: {
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            },
                          }}
                        />
                      )
                    ) : !busy ? (
                      <>
                        <b>Upload image</b>
                        <small>JPG, JPEG or PNG</small>
                      </>
                    ) : null}

                    {hasImage && !busy ? (
                      <button
                        type='button'
                        className='experience-img-remove'
                        aria-label='Remove image'
                        onClick={(e) => {
                          e.stopPropagation()
                          e.preventDefault()
                          pendingRemoveRef.current = () =>
                            handleRemoveImage(setFieldValue)
                          setShowRemoveImageConfirm(true)
                        }}>
                        ×
                      </button>
                    ) : null}

                    <RBForm.Control
                      type='file'
                      accept='image/jpg, image/jpeg, image/png'
                      onChange={(e) => {
                        const target = e.target
                        const files = e.target.files
                        if (files && files?.length > 0) {
                          uploadSelectedLogo(files[0], setFieldValue, () => {
                            if (target) target.value = ''
                          })
                        }
                      }}
                      style={{ display: 'none' }}
                      name='logoFileName'
                      ref={imageRef}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='experience-form-actions'>
              <ExperienceButton
                type='submit'
                variant='primary'
                size='sm'
                isLoading={isSaving}
                disabled={isSaving || busy}>
                Save partner
              </ExperienceButton>
              <ExperienceButton
                type='button'
                variant='outline'
                size='sm'
                onClick={onCancel}
                disabled={isSaving || busy}>
                Cancel
              </ExperienceButton>
            </div>
            <ExperienceConfirmModal
              show={showRemoveImageConfirm}
              onHide={() => {
                setShowRemoveImageConfirm(false)
                pendingRemoveRef.current = null
              }}
              onConfirm={() => {
                const run = pendingRemoveRef.current
                pendingRemoveRef.current = null
                setShowRemoveImageConfirm(false)
                if (run) run()
              }}
              title='Remove this image?'
              message='The image will be cleared. You can upload another.'
              confirmLabel='Delete'
            />
          </form>
        )
      }}
    </Formik>
  )
}
